import React from 'react';
import { Calendar, Clock, Play, Pause, Edit, Trash2, MoreVertical } from 'lucide-react';
import type { Schedule } from '@/types/reporting-v2';

interface ScheduleCardProps {
  schedule: Schedule;
  onExecute?: (scheduleId: string) => void;
  onPause?: (scheduleId: string) => void;
  onResume?: (scheduleId: string) => void;
  onEdit?: (scheduleId: string) => void;
  onDelete?: (scheduleId: string) => void;
}

export default function ScheduleCard({
  schedule,
  onExecute,
  onPause,
  onResume,
  onEdit,
  onDelete,
}: ScheduleCardProps) {
  const scheduleData = schedule as any;
  const scheduleId = scheduleData._id || schedule.schedule_id;
  const nextExecution = scheduleData.next_execution_at || schedule.next_execution;

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle both nested and flat structure
  const totalExecutions = scheduleData.total_executions ?? schedule.execution_stats?.total_executions ?? 0;
  const successfulExecutions = scheduleData.successful_executions ?? schedule.execution_stats?.successful_executions ?? 0;
  const failedExecutions = scheduleData.failed_executions ?? schedule.execution_stats?.failed_executions ?? 0;

  const successRate = totalExecutions > 0
    ? Math.round((successfulExecutions / totalExecutions) * 100)
    : 0;

  return (
    <div className="card card-hover">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{schedule.schedule_name}</h3>
          {schedule.description && (
            <p className="text-sm text-gray-500 mt-1">{schedule.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              schedule.is_active
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            {schedule.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4" />
          <span>Next: {formatDate(nextExecution)}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock className="w-4 h-4" />
          <span className="capitalize">{schedule.frequency}</span>
          <span className="mx-1">•</span>
          <span className="capitalize">{schedule.report_type.replace(/_/g, ' ')}</span>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4 text-xs text-gray-500">
        <div>
          <span className="font-medium text-gray-900">{totalExecutions}</span> runs
        </div>
        <div>
          <span className="font-medium text-green-600">{successfulExecutions}</span> success
        </div>
        <div>
          <span className="font-medium text-red-600">{failedExecutions}</span> failed
        </div>
        <div>
          <span className="font-medium text-gray-900">{successRate}%</span> rate
        </div>
      </div>

      <div className="flex items-center gap-2 pt-3 border-t">
        <button
          onClick={() => onExecute?.(scheduleId)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
          title="Execute Now"
        >
          <Play className="w-4 h-4" />
          Run
        </button>
        {schedule.is_active ? (
          <button
            onClick={() => onPause?.(scheduleId)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
            title="Pause"
          >
            <Pause className="w-4 h-4" />
            Pause
          </button>
        ) : (
          <button
            onClick={() => onResume?.(scheduleId)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-600 hover:bg-green-50 rounded transition-colors"
            title="Resume"
          >
            <Play className="w-4 h-4" />
            Resume
          </button>
        )}
        <button
          onClick={() => onEdit?.(scheduleId)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded transition-colors"
          title="Edit"
        >
          <Edit className="w-4 h-4" />
          Edit
        </button>
        <button
          onClick={() => onDelete?.(scheduleId)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded transition-colors ml-auto"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      </div>
    </div>
  );
}
